import React, { useEffect, useContext, useMemo, memo, type JSX } from 'react';
import { IRutinInfo } from '../../../../Types/RutinInfoType';
import {
  getTextParts,
  IPart,
} from '../../../../BaseComponents/Autocomplete/Utils/AutocompleteUtils';
import EDLinkListItem from '../../../../BaseComponents/List/LinkList/EDLinkListItem';
import EDIcon from '../../../../BaseComponents/Icon/EDIcon';
import isDevelopment, { isUnderTest } from '../../../../Utils/isDevelopment';
import { EDListItemButton } from '../../../../BaseComponents/List/Components/EDListItem';
import EDLinkListItemSecondaryAction from '../../../../BaseComponents/List/LinkList/EDLinkListItemSecondaryAction';
import EDListItemText from '../../../../BaseComponents/List/Components/EDListItemText';
import EDTypography from '../../../../BaseComponents/Typography/EDTypography';
import StartedRoutines, {
  STARTED_ROUTINES_ICON_TYPE,
  STARTED_ROUTINES_TITLE,
} from '../../../../Features/StartPanel/Components/Cards/StartedRoutines/StartedRoutines';
import RutinList from './RutinList';
import { makeStyles } from 'tss-react/mui';
import EDCollapse from '../../../../BaseComponents/Collapsables/Collapse/EDCollapse';
import EDDivider from '../../../../BaseComponents/Divider/EDDivider';
import { ActiveDatabaseContext } from '../../../ActiveDatabaseContext/ActiveDatabaseContext';
import { IDatabase } from '../../../../Features/Login/Contexts/LoginStateContext/LoginStateContext';
import {
  currentRoutingVersion,
  useBaseRouteValue,
} from '../../../../Features/Routing/Utils/routerUtils';
import { IconType } from '../../../../BaseComponents/Icon/Utils/IconDefinitions';

interface IProps {
  activeSubsystemCode: string[];
  activeSubsystemExpanded: boolean;
  setActiveSubsystemExpanded: (value: boolean) => void;
  startedRoutinesExpanded: boolean;
  database: IDatabase;
  routineSearch: string;
  setRoutineSearch: (value: string) => void;
  onToggle: () => void;
  onToggleDelsystem: (delsystem: string) => void;
  setOpen: (delsystem: string[]) => void;
  toggleStartedRoutinesExpanded: () => void;
}

interface IInnerProps extends IProps {
  filteredDelsystem: IDelsystemMatch[];
}

export interface IRutinMatch {
  rutinInfo: IRutinInfo;
  parts: IPart[];
}

interface IDelsystemMatch {
  benamning: string;
  code: string;
  rutinMatches: IRutinMatch[];
  path: string;
  icon?: IconType | JSX.Element;
}

const useStyles = makeStyles()(
  theme =>
    ({
      listItemWrapper: {},
      listItemBorderTop: {
        backgroundColor: theme.customProperties.MenuList.baseBg,
        borderTop: theme.customProperties.MenuList.listBorder,
      },
      listItemBorderTopInactive: {
        borderTop: theme.customProperties.MenuList.inactiveBorder,
      },
      listItemBorderTopActive: {
        backgroundColor: theme.customProperties.MenuList.activeBg,
        borderTop: theme.customProperties.MenuList.listBorder,
      },
      listItemBorderBottom: {
        backgroundColor: theme.customProperties.MenuList.baseBg,
        borderBottom: theme.customProperties.MenuList.listBorder,
      },
      listItemBorderBottomActive: {
        backgroundColor: theme.customProperties.MenuList.activeListBg,
        borderBottom: theme.customProperties.MenuList.listBorder,
      },
    } as const)
);

const DelsystemView: React.FC<React.PropsWithChildren<IInnerProps>> = props => {
  const { system } = useContext(ActiveDatabaseContext);
  const { classes } = useStyles();
  useEffect(() => {
    if (props.routineSearch) {
      props.setOpen(props.filteredDelsystem.map(x => x.code));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.routineSearch, props.filteredDelsystem]);

  const { databasePath, systemPath, usernamePath } = useBaseRouteValue();

  return (
    <React.Fragment>
      <div className={classes.listItemWrapper}>
        <EDListItemButton
          onClick={props.toggleStartedRoutinesExpanded}
          className={
            props.startedRoutinesExpanded
              ? classes.listItemBorderTopActive
              : classes.listItemBorderTopInactive
          }
          minimalPadding
        >
          <EDIcon
            padding={'5px 8px 5px 2px'}
            iconType={STARTED_ROUTINES_ICON_TYPE}
          />
          <EDListItemText
            style={{ maxWidth: '230px' }}
            disableTypography
            primary={
              <EDTypography variant="body2" noWrap>
                {STARTED_ROUTINES_TITLE}
              </EDTypography>
            }
          />
          <EDIcon
            iconType={
              props.startedRoutinesExpanded
                ? 'keyboard_arrow_up'
                : 'keyboard_arrow_down'
            }
          />
        </EDListItemButton>
        <EDCollapse open={props.startedRoutinesExpanded}>
          <div
            onClick={props.onToggle}
            className={classes.listItemBorderBottomActive}
          >
            <StartedRoutines itemsTypographyVariant="body2" />
          </div>
        </EDCollapse>
        <EDDivider />
        <div className={classes.listItemBorderBottom}>
          {props.filteredDelsystem.map(delsystem => {
            if (delsystem.rutinMatches.length === 0) {
              return <></>;
            }
            const delsystemOpen = props.activeSubsystemCode.includes(
              delsystem.code
            );
            return (
              <div key={delsystem.code}>
                <React.Fragment key={delsystem.code}>
                  <EDListItemButton
                    onClick={() => props.onToggleDelsystem(delsystem.code)}
                    className={
                      delsystemOpen
                        ? classes.listItemBorderTopActive
                        : classes.listItemBorderTopInactive
                    }
                    minimalPadding
                  >
                    <EDLinkListItemSecondaryAction
                      to={`/${currentRoutingVersion}/${systemPath}/${usernamePath}/${databasePath}/${delsystem.path}`}
                      onClick={props.onToggle}
                      testId={`delsystemLink-${delsystem.benamning}`}
                    >
                      {delsystem.icon ? (
                        delsystem.icon
                      ) : (
                        <EDIcon iconType="start" />
                      )}
                    </EDLinkListItemSecondaryAction>
                    <EDListItemText
                      style={{ maxWidth: '230px' }}
                      disableTypography
                      primary={
                        <EDTypography variant="body2" noWrap>
                          {delsystem.benamning}
                        </EDTypography>
                      }
                    />
                    <EDIcon
                      iconType={
                        delsystemOpen
                          ? 'keyboard_arrow_up'
                          : 'keyboard_arrow_down'
                      }
                    />
                  </EDListItemButton>
                  <EDCollapse
                    onEntered={() => props.setActiveSubsystemExpanded(true)}
                    open={delsystemOpen}
                  >
                    <div
                      onClick={props.onToggle}
                      className={classes.listItemBorderBottomActive}
                    >
                      <RutinList
                        databaseName={props.database.name}
                        system={system}
                        routineMatches={delsystem.rutinMatches}
                        show={delsystemOpen}
                      />
                    </div>
                  </EDCollapse>
                </React.Fragment>
              </div>
            );
          })}
        </div>
        {isUnderTest() && (
          <EDLinkListItem
            to={`/${currentRoutingVersion}/${systemPath}/${usernamePath}/${databasePath}/debugRutin`}
            text="Debug rutin"
            icon={<EDIcon iconType="explore" />}
            typographyProps={{ variant: 'body2' }}
          />
        )}
        {isDevelopment && (
          <React.Fragment>
            <EDLinkListItem
              to={`/${currentRoutingVersion}/${systemPath}/${usernamePath}/${databasePath}/graphiql`}
              text="GraphiQL"
              icon={<EDIcon iconType="database" />}
              typographyProps={{ variant: 'body2' }}
            />
            <EDLinkListItem
              to={`/${currentRoutingVersion}/${systemPath}/${usernamePath}/${databasePath}/chartexample`}
              text="Graf exempel"
              icon={<EDIcon iconType="pie_chart" />}
              typographyProps={{ variant: 'body2' }}
            />
            <EDLinkListItem
              to={`/${currentRoutingVersion}/${systemPath}/${usernamePath}/${databasePath}/parserDemo`}
              text="Parser Demo"
              inset
              typographyProps={{ variant: 'body2' }}
            />
            <EDLinkListItem
              to={`/${currentRoutingVersion}/${systemPath}/${usernamePath}/${databasePath}/iconDemo`}
              text="Icon Demo"
              inset
              typographyProps={{ variant: 'body2' }}
            />
            <EDLinkListItem
              to={`/${currentRoutingVersion}/${systemPath}/${usernamePath}/${databasePath}/loggerDemo`}
              text="Logger Demo"
              inset
              typographyProps={{ variant: 'body2' }}
            />
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  );
};

const EnhancedDelsystemView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const filteredDelsystem = useMemo<IDelsystemMatch[]>(() => {
    if (!activeDatabaseContext.availableDelsystem) {
      return [];
    }

    return activeDatabaseContext.availableDelsystem.reduce<IDelsystemMatch[]>(
      (arr, delsystem) => {
        const keys = Object.keys(delsystem.rutiner).map(x =>
          Number.parseInt(x, 10)
        );
        const filteredRoutines = keys.reduce<IRutinMatch[]>((a, b) => {
          const rutin = delsystem.rutiner[b];
          if (!activeDatabaseContext.availableRoutines?.[rutin.rutinID]) {
            return a;
          }
          const parts = getTextParts(
            delsystem.rutiner[b].benamning,
            props.routineSearch
          );
          if (
            (parts.length === 1 && props.routineSearch === '') ||
            parts.some(x => x.highlight)
          ) {
            a.push({
              rutinInfo: delsystem.rutiner[b],
              parts: parts,
            });
          }
          return a;
        }, []);
        if (filteredRoutines.length > 0) {
          arr.push({
            benamning: delsystem.benamning,
            code: delsystem.code,
            icon: delsystem.icon,
            path: delsystem.path,
            rutinMatches: filteredRoutines,
          });
        }
        return arr;
      },
      []
    );
  }, [
    activeDatabaseContext.availableDelsystem,
    activeDatabaseContext.availableRoutines,
    props.routineSearch,
  ]);
  return <DelsystemView {...props} filteredDelsystem={filteredDelsystem} />;
};

export default memo(EnhancedDelsystemView);
